/* eslint-disable @angular-eslint/prefer-inject */
import { ShipmentDataService } from '../infrastructure/shipments/shipment.data.service';
import { ShipmentCreate, ShipmentDto, ShipmentRead, ShipmentSearchParams, } from '../entities/shipment.dto';
import { ShipmentStore } from '../infrastructure/shipments/shipment.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { ShipmentMapper } from '../infrastructure/shipments/shipment.mapper';
import { BuildChangeTrackingFacadeFactory, ContactDto, } from '@pf/shared/util-platform';
import { IQueryParams } from '@pf/shared-common';
import { StatusOptions } from '@pf/shared-ui';
import { catchError, map, Observable, of } from 'rxjs';
import { ResourceAuth, ShipmentAddress, ShipmentStatus, } from '@control-tower/platform-wms';
import { WarehouseFacade } from './warehouse.facade';

@Injectable()
export class ShipmentFacade extends AbstractManageEntityFacade<
    ShipmentDto,
    ShipmentRead,
    ShipmentCreate,
    ShipmentSearchParams
> {
    private readonly _warehouseFacade = inject(WarehouseFacade);
    nameField: keyof ShipmentDto = 'trackingNumber';

    statusOptions: StatusOptions = {
        [ShipmentStatus.Open]: { label: 'Open', status: 'primary' },
        [ShipmentStatus.Loaded]: { label: 'Loaded', status: 'secondary' },
        [ShipmentStatus.Shipped]: { label: 'Shipped', status: 'success' },
        [ShipmentStatus.Cancelled]: { label: 'Cancelled', color: 'danger' },
        defaultOption: { status: 'secondary', label: 'Unknown' },
    };

    constructor(
        dataService: ShipmentDataService,
        store: ShipmentStore,
        mapper: ShipmentMapper
    ) {
        super(dataService, null, store, mapper);
    }

    override getDefaultSearchParams$(
        params: IQueryParams<ShipmentDto>
    ): Observable<IQueryParams<ShipmentDto>> {
        return this._warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: ShipmentCreate): void {
        const locationId = this._warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof ShipmentDto, string>> {
        return { trackingNumber: searchText };
    }

    createShipmentFromPackingLists(
        packingListIds: string[],
        carrierId: string,
        shipToAddress: ShipmentAddress,
        shipToContact: ContactDto
    ) {
        return (this.dataService as ShipmentDataService)
            .createFromPackingLists({
                warehouseId: this._warehouseFacade.active!.id as string,
                carrierId,
                shipToAddress,
                packingListIds,
                shipToContact,
            })
            .pipe(
                map(shipment => {
                    return this.mapper.toDTO(shipment);
                })
            );
    }

    ship$(shipmentId: string) {
        return (this.dataService as ShipmentDataService).ship$(shipmentId).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }
}
